import rp from "@reporting/rp-app-host";
import { ComponentAdapter } from "@omegagrid/core";
import { GridSource } from "@omegagrid/grid";
import { MultiContextData, ReportContext, SheetDiff, SpreadsheetViewOptions } from "@reporting/rp-types/analytics";
import { UidMultiContextDataItem } from "../../model/itemUid";
import { ReportStore } from "../../model/reportStore";
import { openContextFilter } from "../../actions";
import { AnalyticsApplication } from "../..";
import { getGridSource } from "./tableAdapter";
import { applySheetDiff, assignGridNames, sanitize } from "./sheetDiff";

const app = () => rp.applications.get('analytics') as AnalyticsApplication;

/** default per-sheet grid options of a data-connected sheet (legacy parity) */
const DATA_SHEET_OPTIONS = {editable: true, rowNumbers: true, colLetters: true};

export type SpreadsheetItemSource = {
	/** runtime item uid, carried into the grid model props */
	uid: string;
	/** current index of the item within the context items */
	index: number;
	/** unique sheet name shown on the tab */
	name: string;
	item: UidMultiContextDataItem;
	/** true for a sheet not connected to any report (its state lives in item.sheet_model) */
	freeForm: boolean;
}

export type SpreadsheetSheetSource = {
	/** grid source ready to display (named, diff applied, props.uid set) */
	source: GridSource;
	/** pristine named server grid the diff was applied to; null for free-form sheets */
	base: GridSource | null;
}

/** Excel sheet name rules: strip \ / * [ ] : ? " and truncate to 31 chars */
export function escapeSheetName(name: string): string {
	return (name ?? '').replace(/[\\/*[\]:?"]/g, '').trim().slice(0, 31);
}

export abstract class SpreadsheetAdapter extends ComponentAdapter {

	abstract readonly reportContext: ReportContext;

	/** backing store of the spreadsheet context */
	get store(): ReportStore {
		return null;
	}

	get contextData(): MultiContextData {
		return this.reportContext?.context as MultiContextData;
	}

	get viewOptions(): SpreadsheetViewOptions {
		return (this.contextData?.view_options ?? {}) as SpreadsheetViewOptions;
	}

	abstract getItems(): SpreadsheetItemSource[];

	/** resolve the displayable grid source of one sheet (report data + diff, or the free-form model) */
	abstract getSheetSource(item: SpreadsheetItemSource): Promise<SpreadsheetSheetSource>;

	/** persist the modifications of a sheet: the diff of a data sheet or the full model of a free-form sheet */
	updateSheet(uid: string, patch: {sheet_diff?: SheetDiff | null, sheet_model?: Record<string, any>}) { }

	renameItem(uid: string, title: string) { }

	removeItem(uid: string) { }

	duplicateItem(uid: string) { }

	/** append a free-form sheet, returns its uid */
	addFreeFormItem(title?: string): string {
		return null;
	}

	/** persist the og-editor tab/split layout */
	updateLayout(layout: Record<string, any>) { }

	onAction(action: string, item?: SpreadsheetItemSource) { }

}

/**
 * Adapter of a stored spreadsheet context. Reads come from the store draft
 * (a fresh immutable snapshot after every change), writes go through the
 * store mutation API - the adapter itself holds no state.
 */
export class MultiContextSpreadsheetAdapter extends SpreadsheetAdapter {

	constructor(private readonly _store: ReportStore) { super() }

	get store(): ReportStore {
		return this._store;
	}

	get reportContext(): ReportContext {
		return this._store.context;
	}

	get contextData(): MultiContextData {
		return this._store.contextData as MultiContextData;
	}

	getItems(): SpreadsheetItemSource[] {
		const used = new Set<string>();
		return ((this.contextData?.items ?? []) as UidMultiContextDataItem[]).map((item, index) => {
			let name = escapeSheetName(item.title) || `Sheet ${index + 1}`;
			if (used.has(name)) {
				let n = 2;
				while (used.has(`${name} (${n})`)) n++;
				name = `${name} (${n})`;
			}
			used.add(name);
			return {
				uid: item.uid,
				index: index,
				name: name,
				item: item,
				freeForm: !item.id_context && !item.context,
			};
		});
	}

	async getSheetSource(item: SpreadsheetItemSource): Promise<SpreadsheetSheetSource> {
		let source: GridSource;
		let base: GridSource = null;

		if (item.freeForm) {
			source = (sanitize(item.item.sheet_model) ?? {}) as GridSource;
		} else {
			const subcontext = await app().contextBroker.getSubcontext(this._store.context, item.index);
			if (!subcontext) throw new Error(`Missing context of sheet "${item.name}"`);
			const result = await app().reportBroker.getReportData(subcontext, {
				parentContext: this._store.context,
				history: item.index === 0,
			});
			base = assignGridNames(getGridSource(result, subcontext));
			base.options = {...base.options, ...DATA_SHEET_OPTIONS};
			source = applySheetDiff(base, item.item.sheet_diff);
		}

		source.name = item.name;
		source.props = {...source.props, uid: item.uid};
		if (item.item.style?.color) source.tab = {...source.tab, color: item.item.style.color as string};
		return {source, base};
	}

	updateSheet(uid: string, patch: {sheet_diff?: SheetDiff | null, sheet_model?: Record<string, any>}) {
		this._store.updateItemSheet(uid, patch);
	}

	renameItem(uid: string, title: string) {
		this._store.renameItem(uid, title);
	}

	removeItem(uid: string) {
		this._store.removeItem(uid);
	}

	duplicateItem(uid: string) {
		this._store.duplicateItem(uid);
	}

	addFreeFormItem(title?: string): string {
		return this._store.addItem({title: title, sheet_model: {}});
	}

	updateLayout(layout: Record<string, any>) {
		this._store.updateViewOptions(viewOptions => {
			viewOptions.layout = layout;
		});
	}

	onAction(action: string, item?: SpreadsheetItemSource) {
		switch (action) {
			case 'filter':
				openContextFilter(this._store.idContext, item?.index);
				break;
		}
	}

}
